import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// Public pages
import Midgard from './pages/Midgard';
import LearnMore from './pages/LearnMore';
import Gatekeeper from './pages/Gatekeeper';

// Onboarding
import Onboarding from './pages/Onboarding';

// Protected pages
import Asgard from './pages/Asgard';
import ForgeOfOdin from './pages/ForgeOfOdin';
import RunePrinter from './pages/RunePrinter';
import BifrostConsole from './pages/BifrostConsole';
import Allsight from './pages/Allsight';
import LokisChamber from './pages/LokisChamber';
import Valhalla from './pages/Valhalla';
import Yggdrasil from './pages/Yggdrasil';

// Circulation & Patron Management
import OdinsRavens from './pages/OdinsRavens';       // Overdue notices processor
import Mjolnir from './pages/Mjolnir';               // Damage notices generator
import Gjallarhorn from './pages/Gjallarhorn';       // Library account letters & labels
import UrdsWell from './pages/UrdsWell';             // Roster management
import Gullveig from './pages/Gullveig';             // Payment & lost book management
import HermodsPath from './pages/HermodsPath';       // Social media command centre

// Booking System
import NornsWeave from './pages/NornsWeave';         // Public booking portal (password only)
import CalendarBooking from './pages/CalendarBooking'; // Magic link booking management
import AdminNornsWeave from './pages/AdminNornsWeave'; // Admin booking dashboard

// Special Collections
import MimirsWell from './pages/MimirsWell';         // Ephemera cataloging

// The login moved from /gatekeeper to /login. The old address keeps working, and keeps whatever
// was asked of it — ?signup=true is how the sign-up form is reached.
function ToLogin() {
  const { search } = useLocation();
  return <Navigate to={`/login${search}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Midgard />} />
          <Route path="/learn-more" element={<LearnMore />} />
          <Route path="/login" element={<Gatekeeper />} />

          {/* Old URLs redirect, never 404. The front pages name the dashboard /desk, which is
              what it is called on the org build; here it is still /asgard. */}
          <Route path="/gatekeeper" element={<ToLogin />} />
          <Route path="/desk" element={<Navigate to="/asgard" replace />} />
          
          {/* Public Calendar Portal - Password protected only, no account needed */}
          <Route path="/calendar" element={<NornsWeave />} />
          <Route path="/calendar/booking/:token" element={<CalendarBooking />} />

          {/* Legacy route redirect */}
          <Route path="/norns-weave" element={<NornsWeave />} />
          
          {/* Onboarding Route - Protected but allows incomplete onboarding */}
          <Route path="/onboarding" element={
            <PrivateRoute requireOnboarding={false}>
              <Onboarding />
            </PrivateRoute>
          } />
          
          {/* Protected Routes - Require completed onboarding */}
          <Route path="/asgard" element={
            <PrivateRoute>
              <Asgard />
            </PrivateRoute>
          } />
          <Route path="/forge" element={
            <PrivateRoute>
              <ForgeOfOdin />
            </PrivateRoute>
          } />
          <Route path="/runes" element={
            <PrivateRoute>
              <RunePrinter />
            </PrivateRoute>
          } />
          <Route path="/bifrost" element={
            <PrivateRoute>
              <BifrostConsole />
            </PrivateRoute>
          } />
          <Route path="/allsight" element={
            <PrivateRoute>
              <Allsight />
            </PrivateRoute>
          } />
          <Route path="/loki" element={
            <PrivateRoute>
              <LokisChamber />
            </PrivateRoute>
          } />
          <Route path="/valhalla" element={
            <PrivateRoute>
              <Valhalla />
            </PrivateRoute>
          } />
          <Route path="/yggdrasil" element={
            <PrivateRoute>
              <Yggdrasil />
            </PrivateRoute>
          } />
          
          {/* Circulation & Patron Management Routes */}
          
          {/* Odin's Ravens - Overdue notices */}
          <Route path="/odins-ravens" element={
            <PrivateRoute>
              <OdinsRavens />
            </PrivateRoute>
          } />
          
          {/* Mjolnir - Damage notices */}
          <Route path="/mjolnir" element={
            <PrivateRoute>
              <Mjolnir />
            </PrivateRoute>
          } />
          
          {/* Gjallarhorn - Library account letters & labels */}
          <Route path="/gjallarhorn" element={
            <PrivateRoute>
              <Gjallarhorn />
            </PrivateRoute>
          } />

          {/* Urd's Well - Roster management */}
          <Route path="/urds-well" element={
            <PrivateRoute>
              <UrdsWell />
            </PrivateRoute>
          } />

          {/* Gullveig - Payment & lost book management */}
          <Route path="/gullveig" element={
            <PrivateRoute>
              <Gullveig />
            </PrivateRoute>
          } />

          {/* Hermod's Path - Social media command centre */}
          <Route path="/hermods-path" element={
            <PrivateRoute>
              <HermodsPath />
            </PrivateRoute>
          } />

          {/* Norns' Weave Admin - Library booking management */}
          <Route path="/admin/norns-weave" element={
            <PrivateRoute>
              <AdminNornsWeave />
            </PrivateRoute>
          } />

          {/* Mímir's Well - Special Collections & Ephemera */}
          <Route path="/mimir" element={
            <PrivateRoute>
              <MimirsWell />
            </PrivateRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;